import { useEffect, useState, type FormEvent } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  FormField,
  Heading,
  Text,
} from "@lendcord/concord";
import { OtpBoxes } from "@/auth/components/OtpBoxes";
import { useConfirmVerificationOtpMutation, useSendVerificationOtpMutation } from "@/features/onboarding/api/onboarding.api";
import { isQueryApiError } from "@/app/store/baseApi";
import { useToast } from "@/app/toast";

interface VerificationStepProps {
  applicationId: string;
}

// Reviewer-triggered re-verification (Form 1) — a popup rather than an inline
// section, so the review page stays a single-glance summary until the
// reviewer actually starts verifying. On success, confirmVerificationOtp's
// invalidatesTags refetches the application and its fresh `state` (VERIFIED)
// routes ApplicationReviewFlow into LoanTermsStep automatically, which
// unmounts this component — no local "close the dialog" call needed there.
export function VerificationStep({ applicationId }: VerificationStepProps) {
  const intl = useIntl();
  const { showToast } = useToast();
  const [sendOtp, { isLoading: isSending }] = useSendVerificationOtpMutation();
  const [confirmOtp, { isLoading: isConfirming }] = useConfirmVerificationOtpMutation();
  const [open, setOpen] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [phoneOtp, setPhoneOtp] = useState("");
  const [emailOtp, setEmailOtp] = useState("");
  const [errors, setErrors] = useState<{ phone?: string; email?: string }>({});

  // Keyed on `open` (not routed through onOpenChange) so the form resets no
  // matter how the dialog closes — Escape, the overlay, or the X button —
  // same pattern as ChangePasswordDialog.
  useEffect(() => {
    if (!open) {
      setOtpSent(false);
      setPhoneOtp("");
      setEmailOtp("");
      setErrors({});
    }
  }, [open]);

  async function handleSend() {
    setErrors({});
    try {
      await sendOtp(applicationId).unwrap();
      setOtpSent(true);
    } catch (error) {
      showToast({
        status: "error",
        title: intl.formatMessage({ id: "lender.onboarding.review.otpSendError" }),
        description: isQueryApiError(error) ? error.message : undefined,
      });
    }
  }

  async function handleConfirm(event: FormEvent) {
    event.preventDefault();
    if (phoneOtp.length !== 6 || emailOtp.length !== 6) {
      const required = intl.formatMessage({ id: "lender.onboarding.review.otpRequiredError" });
      setErrors({
        phone: phoneOtp.length !== 6 ? required : undefined,
        email: emailOtp.length !== 6 ? required : undefined,
      });
      return;
    }
    setErrors({});
    try {
      await confirmOtp({
        id: applicationId,
        payload: {
          otps: [
            { type: "EMAIL", otp: emailOtp },
            { type: "PHONE", otp: phoneOtp },
          ],
        },
      }).unwrap();
    } catch (error) {
      if (isQueryApiError(error) && error.code === "LEND-010") {
        // "OTP not valid for TYPE: EMAIL" / "...PHONE" — the other channel's
        // already-correct code isn't lost, only flag the wrong one.
        const incorrect = intl.formatMessage({ id: "lender.onboarding.review.otpIncorrectError" });
        setErrors(error.message.toUpperCase().includes("EMAIL") ? { email: incorrect } : { phone: incorrect });
      } else if (isQueryApiError(error) && error.code === "LEND-011") {
        showToast({ status: "error", title: intl.formatMessage({ id: "lender.onboarding.review.otpExpiredError" }) });
      } else {
        showToast({
          status: "error",
          title: intl.formatMessage({ id: "lender.onboarding.review.otpGenericError" }),
          description: isQueryApiError(error) ? error.message : undefined,
        });
      }
    }
  }

  return (
    <div className="mt-6 flex flex-col gap-4 rounded-md border border-border bg-surface-raised p-6">
      <div>
        <Heading level={4} className="text-accent-600">
          <FormattedMessage id="lender.onboarding.review.verifyTriggerHeading" />
        </Heading>
        <Text as="p" size="sm" variant="muted" className="mt-1">
          <FormattedMessage id="lender.onboarding.review.verificationDescription" />
        </Text>
      </div>
      <div>
        <Button type="button" onClick={() => setOpen(true)}>
          <FormattedMessage id="lender.onboarding.review.verifyTriggerButton" />
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <FormattedMessage id="lender.onboarding.review.verifyTriggerButton" />
            </DialogTitle>
          </DialogHeader>

          {!otpSent ? (
            <div>
              <Button type="button" onClick={handleSend} disabled={isSending}>
                {isSending ? (
                  <FormattedMessage id="lender.onboarding.review.sendingOtpButton" />
                ) : (
                  <FormattedMessage id="lender.onboarding.review.sendOtpButton" />
                )}
              </Button>
            </div>
          ) : (
            <form onSubmit={handleConfirm} noValidate className="flex flex-col gap-5">
              <FormField
                label={intl.formatMessage({ id: "lender.onboarding.review.phoneCodeLabel" })}
                error={errors.phone}
                required
              >
                <OtpBoxes value={phoneOtp} onChange={setPhoneOtp} />
              </FormField>
              <FormField
                label={intl.formatMessage({ id: "lender.onboarding.review.emailCodeLabel" })}
                error={errors.email}
                required
              >
                <OtpBoxes value={emailOtp} onChange={setEmailOtp} />
              </FormField>
              <Text as="p" size="xs" variant="muted">
                <FormattedMessage id="lender.onboarding.review.crifConsentText" />
              </Text>
              <div className="flex gap-3">
                <Button type="submit" disabled={isConfirming}>
                  {isConfirming ? (
                    <FormattedMessage id="lender.onboarding.review.confirmingButton" />
                  ) : (
                    <FormattedMessage id="lender.onboarding.review.confirmButton" />
                  )}
                </Button>
                <Button type="button" variant="secondary" onClick={handleSend} disabled={isSending}>
                  <FormattedMessage id="lender.onboarding.review.resendOtpButton" />
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
